<template>
    <DxDataGrid ref="datagrid" :data-source="dataSource" :show-borders="false" :show-row-lines="true"
        :show-footer="true" :show-column-lines="true" :hover-state-enabled="true" :height="contentHeight"
        :auto-expand-all="true" :word-wrap-enabled="true" row-alternation-enabled :on-content-ready="onContentReady"
        :remote-operations="true">
        <DxScrolling mode="infinite" row-rendering-mode="virtual" />
        <DxPaging :page-size="pageSize" />
        <DxPager :show-navigation-buttons="true" :show-info="true" :show-page-size-selector="true"
            :allowed-page-sizes="[10, 25, 50, 100]" />
        <DxRemoteOperations :group-paging="true" :paging="true" :filtering="true" />
        <DxColumn data-field="ContractId" :caption="$t('servicesystem.common_objects.contract.contract')"
            :min-width="60" width="70" />
        <DxColumn :allow-sorting="false" data-field="__detailText" caption="Produkttekst" :min-width="300" width="35%"
            cell-template="detailTextTemplate" />
        <DxColumn data-field="__customerDisplayText" :caption="$t('servicesystem.common_objects.contract.customer')"
            :allow-resizing="false" :min-width="250" width="auto" />
        <DxColumn :allow-sorting="false" v-if="showDates" data-field="__NextInvoiceStartDateDisplay" caption="Neste"
            :min-width="90" width="90" />
        <DxColumn :allow-sorting="false" v-if="showDates" data-field="__NextInvoiceEndDateDisplay" caption="Til"
            :min-width="90" width="90" />
        <DxColumn :allow-sorting="false" v-if="showDates" data-field="__ContractStartDisplay" caption="Start"
            :min-width="90" :width="90" />
        <DxColumn :allow-sorting="false" v-if="showDates" data-field="__ContractEndDisplay" caption="Slutt"
            :min-width="90" :width="90" />
        <DxColumn :allow-sorting="true" data-field="Qty" caption="Antall" :min-width="35" :width="55"
            alignment="right" />
        <DxColumn :allow-sorting="false" data-field="__unitPrice" caption="Stykkpris" alignment="right"
            cell-template="unitPriceTemplate" />
        <DxColumn :allow-sorting="false" data-field="__monthlyPriceDisplay" caption="Pris pr måned" alignment="right"
            cell-template="monthlyPriceTemplate" />
        <DxColumn :allow-sorting="false" data-field="__yearlyPriceDisplay" cell-template="yearlyPriceTemplate"
            caption="Pris pr år" alignment="right" :min-width="75" />
        <DxSummary :recalculate-while-editing="true">
            <DxTotalItem column="__detailText" summary-type="sum" display-format="{0}"
                :customize-text="customizeSummaryText" />
        </DxSummary>
        <DxToolbar>
            <DxItem location="before">
                <template #default>
                    <span class="ml-2">
                        {{ summary.totalCount }} items found.
                        <span v-if="totalCount != summary.totalCount" class="ml-1">
                            (Showing {{ totalCount }} items)
                        </span>
                    </span>
                </template>
            </DxItem>
            <DxItem name="searchPanel" location="after" class="pr-2" />
        </DxToolbar>
        <DxSearchPanel :visible="showSearch" :width="240" placeholder="Search..." :text.sync="searchText" />
        <template #detailTextTemplate="{ data }">
            <div :class="subContractDetailClass(data.data)">
                <div :class="[terminatedClass(data.data)]">
                    {{ data.value }}
                    <v-tooltip v-if="data.data.InvoiceComment" bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-icon small class="ml-1" v-bind="attrs" v-on="on">mdi-note-outline</v-icon>
                        </template>
                        <span>{{ data.data.InvoiceComment }}</span>
                    </v-tooltip>
                </div>
                <span> {{ data.data.ContractDetailComment }}</span>
            </div>
        </template>

        <template #commentTemplate="{ data }">
            <div :class="['ellipsis', terminatedClass(data.data)]">
                {{ data.data.ContractDetailComment || data.data.InvoiceComment ||
                    data.data.ContractDetailArticleSalesText
                    || '' }}
            </div>
        </template>

        <template #unitPriceTemplate="{ data }">
            <template v-if="data.data.PriceModelId == 2">
                {{ data.data.__unitPriceDisplay }}
            </template>
            <template v-else>
                <span v-if="data.data.__yearlyPriceDisplay">{{ data.data.__yearlyPriceDisplay }}
                    <span :class="[getYearlyStatusClass(data.data)]">
                        {{ getYearlyText(data.data) }}</span>
                    /år</span>
            </template>
        </template>

        <template #monthlyPriceTemplate="{ data }">
            <template v-if="data.data.PriceModelId == 1">
                <span>
                    {{ data.value }}
                    <span :class="[getMonthlyStatusClass(data.data)]">
                        {{ getMonthlyText(data.data) }}</span>
                </span>
            </template>
        </template>
        <template #yearlyPriceTemplate="{ data }">
            <template v-if="data.data.PriceModelId == 1">
                <span>
                    {{ data.value }}
                    <span :class="[getYearlyStatusClass(data.data)]">
                        {{ getYearlyText(data.data) }}</span>
                </span>
            </template>
        </template>
    </DxDataGrid>
</template>

<script>
import clientAPI from "@/api/ClientAPIMixin.js";
import FilterQueryMixin from "@/helpers/Mixins/FilterQueryMixin.js";
import DxDataGrid, {
    DxColumn,
    DxItem,
    DxPager,
    DxPaging,
    DxRemoteOperations, DxScrolling, DxSearchPanel, DxSummary, DxTotalItem, DxToolbar
} from 'devextreme-vue/data-grid';
import ContractMixin from "@/helpers/Mixins/ContractMixin";
import SettingsMixin from '@/views/admin/settings/default-settings/SettingsMixin';
import CustomStore from "devextreme/data/custom_store";
export default {
    name: 'ContractDataGrid',
    props: {
        preview: {
            type: Object,
            required: false,
            default: () => ({
                mode: null,
                fixedPrice: 0,
                dynamicPricingPercentage: null
            })
        },
        summary: {
            type: Object,
            required: false,
            default: () => ({
                totalCount: 0,
                totalSum: 0
            })
        },
        filter: {
            type: Object,
            required: false,
            default: () => ({})
        },
        items: {
            type: Array,
            required: false
        },
        contentHeight: {
            type: String,
            required: false,
            default: '300px'
        }
    },
    components: {
        DxSearchPanel,
        DxPaging,
        DxScrolling,
        DxPager,
        DxRemoteOperations,
        DxDataGrid,
        DxColumn,
        DxSummary,
        DxTotalItem,
        DxItem,
        DxToolbar
    },
    mixins: [clientAPI, FilterQueryMixin, ContractMixin, SettingsMixin],
    mounted() {
        const vueSelf = this;
        this.dataSource = new CustomStore({
            useDefaultSearch: true,
            cacheRawData: false,
            load: function (options) {
                return clientAPI.methods.getContractDetailsWithLatestPriceFromFilter({
                    ...vueSelf.filter,
                    Sort: options.sort?.map(s => ({
                        Field: s.selector,
                        Direction: s.desc ? 1 : 0
                    })) ?? null,
                    SearchWord: vueSelf.searchText,
                    Skip: options.skip ?? 0,
                    Limit: options.take ?? vueSelf.pageSize
                })
                    .then(response => {
                        vueSelf.totalSum = response.TotalSum;
                        vueSelf.totalCount = response.TotalCount;
                        return {
                            data: response.Content.map(e => vueSelf.prepareRow(e)),
                            totalCount: response.TotalCount
                        }
                    })
                    .catch(error => {
                        console.error("DataSource load error:", error);
                        throw error;
                    });
            },
            byKey: (key) => {
                return key.Id;
            },
        })
    },
    data() {
        return {
            totalCount: 0,
            showDates: false,
            showSearch: true,
            searchText: '',
            totalSum: 0,
            pageSize: 25,
            groupByCustomer: true,
            dataSource: null,
        };
    },
    methods: {
        getYearlyText(detail) {
            let newYearly = 0;
            if (this.preview.mode === 'fixed' && this.preview.fixedPrice !== null) {
                newYearly = (this.preview.fixedPrice * (detail.Qty || 1) * 12) - detail.__yearlyPrice;
            } else if (this.preview.mode === 'dynamic' && this.preview.dynamicPricingPercentage !== null) {
                newYearly = detail.__yearlyPrice * (this.preview.dynamicPricingPercentage / 100);
            } else {
                return '';
            }

            return `(${sign(newYearly)}${newYearly.toFixed(2)})`;
        },
        getMonthlyText(detail) {
            let newMonthly = 0;

            if (this.preview.mode === 'fixed' && this.preview.fixedPrice !== null) {
                newMonthly = this.preview.fixedPrice - detail.__monthlyPrice;
            } else if (this.preview.mode === 'dynamic' && this.preview.dynamicPricingPercentage !== null) {
                newMonthly = detail.__monthlyPrice * (this.preview.dynamicPricingPercentage / 100);
            } else {
                return '';
            }
            return `(${sign(newMonthly)}${newMonthly.toFixed(2)})`;
        },
        getMonthlyStatusClass(detail) {
            if (this.preview.mode === 'fixed' && this.preview.fixedPrice !== null) {
                const newMonthly = this.preview.fixedPrice - detail.__monthlyPrice;
                if (newMonthly >= 0) {
                    return 'positive-status';
                } else if (newMonthly < 0) {
                    return 'negative-status';
                }
            }
            else if (this.preview.mode === 'dynamic' && this.preview.dynamicPricingPercentage !== null) {
                const newMonthly = detail.__monthlyPrice * (this.preview.dynamicPricingPercentage / 100);
                if (newMonthly >= 0) {
                    return 'positive-status';
                } else if (newMonthly < 0) {
                    return 'negative-status';
                }
            }
            return '';
        },
        getYearlyStatusClass(detail) {
            if (this.preview.mode === 'fixed' && this.preview.fixedPrice !== null) {
                const newYearly = (this.preview.fixedPrice * 12) - detail.__yearlyPrice;
                if (newYearly >= 0) {
                    return 'positive-status';
                } else if (newYearly < 0) {
                    return 'negative-status';
                }
            }
            else if (this.preview.mode === 'dynamic' && this.preview.dynamicPricingPercentage !== null) {
                const newYearly = detail.__yearlyPrice * (this.preview.dynamicPricingPercentage / 100);
                if (newYearly >= 0) {
                    return 'positive-status';
                } else if (newYearly < 0) {
                    return 'negative-status';
                }
            }
            return '';
        },
        refresh() {
            if (this.dataSource) {
                this.$refs.datagrid.instance.refresh();
                this.$refs.datagrid.instance.repaint();
            }
        },
        customizeSummaryText() {
            let priceIncreasePreview = '';
            if (this.preview.mode === 'fixed') {
                priceIncreasePreview = `(${this.formatMoney((this.preview.fixedPrice * this.summary.totalCount * 12) - this.summary.totalSum)})`;
            } else if (this.preview.mode === 'dynamic') {
                priceIncreasePreview = `(${this.formatMoney(this.summary.totalSum * (this.preview.dynamicPricingPercentage / 100))})`;
            }
            return `${this.formatMoney(this.summary.totalSum)} ${this.$t('servicesystem.common_objects.contract.total_yearly')} ${priceIncreasePreview}`;
        },
        unitPrice(detail) {
            if (!detail) return null;
            if (detail.PriceModelId == 2) {
                return detail.Price ?? detail.HighPrice ?? detail.LowPrice ?? null;
            }
            return null;
        },
        monthlyPrice(detail) {
            if (!detail) return null;
            if (detail.PriceModelId != 1) return null; // PerMonth only
            const prices = detail.ContractPrices || [];
            if (!prices.length) return detail.Price ?? null;
            const now = new Date();
            // Do NOT mutate source arrays during render; work on copies only
            const pick = prices
                .map(p => ({ ...p, _d: new Date(p.PriceDate) }))
                .filter(p => !isNaN(p._d))
                .sort((a, b) => b._d - a._d)
                .find(p => p._d <= now) ||
                prices.slice().sort((a, b) => new Date(b.PriceDate) - new Date(a.PriceDate))[0];
            return pick?.NewPrice ?? null;
        },
        onContentReady() {
            this.$nextTick(() => {
                this.$refs.datagrid.instance.updateDimensions();
            });
        },
        onOptionChanged(e) {
            // Automatically disable grouping when user sorts by clicking a column
            if (e.name === 'columns' && e.fullName.includes('.sortOrder')) {
                if (this.groupByCustomer) {
                    this.groupByCustomer = false;
                }
            }
        },
        onUpdate(detail) {
            const updated = this.prepareRow(detail);
            const existing = this.findRowById(updated.Id);
            if (existing) {
                Object.assign(existing, updated);
            }
            // Refresh the DataGrid to recalculate totals
            if (this.$refs.datagrid && this.$refs.datagrid.instance) {
                this.$refs.datagrid.instance.refresh();
            }
        },
        subContractDetailClass(row) {
            return row.ParentDetailId != null ? 'pl-4' : '';
        },
        terminatedClass(row) {
            return row.ContractTerminatedDate && new Date(row.ContractTerminatedDate) < new Date() ? 'contract-terminated' : '';
        },
        fetchSettings() {
            var groups = new Set(this.settingList.map(obj => obj.group));
            this.isLoading = true;

            clientAPI.methods.getSettingsListByGroup({ groups: [...groups] }).then(response => {
                //map the setting values from the reponse onto the setting list items
                this.settingList.forEach(setting => {
                    var newVal = response[setting.group?.toLowerCase()][setting.settingKey];
                    this.settingValueFromString(setting, newVal)
                });
                //also re-fetch globalsettings in the background if necessary
                if (this.settingList.some(x => x.globalSettingsKey)) {
                    this.$store.dispatch("getUserInfo", true);
                }
                this.isLoading = false;
                this.initiated = true;
            });
        },
        flattenTree(items) {
            const result = [];
            const flatten = (item) => {
                const children = item.SubContractDetails;
                const flat = { ...item };
                delete flat.SubContractDetails;
                result.push(flat);
                if (Array.isArray(children) && children.length > 0) {
                    children.forEach(child => {
                        child.ParentDetailId = item.Id;
                        child.Customer = item.Machine?.Customer || item.Customer; // inherit customer for grouping
                        child.CustomerId = item.Machine?.Customer?.Id || item.CustomerId;
                        child.InvoiceCustomer = item.InvoiceCustomer; // inherit invoice customer for grouping
                        child.InvoiceCustomerId = item.InvoiceCustomerId;
                        flatten(child);
                    });
                }
            };
            items.forEach(item => flatten(item));
            return result;
        },

        onDraftAdded(created) {
            if (created) {
                const prepared = this.prepareRow(created);
                this.rows.unshift(prepared);

                // If the edit pane is open on this parent, inject the new child into it via ref
                if (prepared.ParentDetailId && String(prepared.ParentDetailId) === String(this.editDetailId) && this.$refs.editPane) {
                    this.$refs.editPane.insertChildDetail(created);
                }

                // Clear child-parent selection and close dialog
                this.newChildParentDetail = null;
            }
        },

        handleDialogClosed() {
            this.newChildParentDetail = null;
            this.dialogCloseTrigger++;
        },

        onDetailDeleted({ id }) {
            if (!id) return;
            this.removeRowById(id);
        },

        openNewDialogForParent(parentDetail) {
            this.newChildParentDetail = parentDetail;
            this.dialogTrigger++;
        },

        findRowById(id) {
            return this.rows.find(x => x && x.Id === id);
        },

        removeRowById(id) {
            const idx = this.rows.findIndex(x => x && x.Id === id);
            if (idx >= 0) {
                this.rows.splice(idx, 1);
            }
        },

        formatMoney(v) {
            if (v == null || isNaN(v)) return '';
            try {
                return Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            } catch { return v; }
        },

        prepareRow(detail) {
            // Shallow copy to avoid mutating original reference
            const row = { ...detail };

            // Detail text
            row.__detailText = this.getDetailText(row);
            row.__customerId = detail.CustomerId || detail.Machine?.Customer?.Id || detail?.InvoiceCustomerId || this.contract?.CustomerId;
            row.__customerName = detail.Customer?.Name || detail.Machine?.Customer?.Name || detail.InvoiceCustomer?.Name || this.contract?.Customer?.Name;
            row.__customerDisplayText = `${row.__customerId} ${row.__customerName}`;
            // Dates (preformatted display strings)
            row.__NextInvoiceStartDateDisplay = this.i18nFormatDate(row.NextInvoiceStartDate);
            row.__NextInvoiceEndDateDisplay = this.i18nFormatDate(row.NextInvoiceEndDate);
            row.__ContractTerminatedDateDisplay = this.i18nFormatDate(row.ContractTerminatedDate);
            row.__ContractStartDisplay = this.i18nFormatDate(row.ContractStart);
            row.__ContractEndDisplay = this.i18nFormatDate(row.ContractEnd);

            // Prices
            let monthly = null;
            let unit = null;
            let yearly = null;

            const qty = row.Qty != null ? Number(row.Qty) : null;
            if (row.PriceModelId == 1) {
                monthly = this.monthlyPrice(row) * qty;
                yearly = monthly != null ? monthly * 12 : null;
            } else if (row.PriceModelId == 2) {
                unit = this.unitPrice(row);
                yearly = unit != null && qty != null ? unit * qty : null;
            }

            row.__monthlyPrice = monthly;
            row.__unitPrice = unit;
            row.__yearlyPrice = yearly;

            row.__monthlyPriceDisplay = monthly != null ? this.formatMoney(monthly) : '';
            row.__unitPriceDisplay = unit != null ? this.formatMoney(unit) : '';
            row.__yearlyPriceDisplay = yearly != null ? this.formatMoney(yearly) : '';

            // Children (tree rows): prepare recursively so nested rows have precomputed fields
            if (Array.isArray(row.SubContractDetails) && row.SubContractDetails.length) {
                row.SubContractDetails = row.SubContractDetails.map(child => this.prepareRow(child));
            }
            return row;
        }
    },
    watch: {
        filter: {
            immediate: true,
            handler() {
                if (this.dataSource) {
                    this.$refs.datagrid.instance.refresh();
                }
            }
        },
        items: {
            immediate: true,
            handler() {
                if (this.dataSource) {
                    this.$refs.datagrid.instance.refresh();
                }
            }
        },
        preview: {
            immediate: false,
            handler() {
                if (this.dataSource) {
                    this.$refs.datagrid.instance.repaint();
                }
            }
        },
        summary: {
            immediate: false,
            handler() {
                if (this.dataSource) {
                    this.$refs.datagrid.instance.repaint();
                }
            }
        }
    }
};

function sign(value) {
    return value >= 0 ? '+' : '';
}
</script>
<style lang='scss' scoped>
.ellipsis {
    display: inline-block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

/* DevExtreme TreeList customization */
::v-deep .dx-treelist {
    font-size: 14px;
}

::v-deep .dx-treelist-rowsview .dx-row {
    cursor: pointer;
}

::v-deep .dx-treelist .dx-row>td {
    padding-top: 4px;
    padding-bottom: 4px;
}

::v-deep .dx-treelist .dx-header-row>td {
    font-weight: 600;
    background-color: #f5f5f5;
}

::v-deep .dx-treelist-text-content {
    overflow: hidden;
    text-overflow: ellipsis;
}

::v-deep .dx-command-expand {
    width: 30px;
}

::v-deep .dx-datagrid-group-space {
    width: 2px !important;
    /* Adjust the value as needed */
    min-width: 2px !important;
}

.contract-terminated {
    text-decoration: line-through;
}

.link:not(.contract-terminated) {
    color: #1976d2;
    font-weight: bold;
}

::v-deep .dx-datagrid-headers {
    height: 30px;
}

::v-deep .dx-header-row>td>.dx-datagrid-text-content {
    white-space: nowrap !important;
    text-overflow: ellipsis;
}

::v-deep .dx-datagrid-headers .dx-datagrid-content {
    height: 30px;
}

.positive-status {
    color: var(--v-success-base);
}

.negative-status {
    color: var(--v-error-base);
}
</style>
